// src/Api.evidences.ts
import { AuthenticationError } from "@auth0/auth0-react";
import axios from "axios";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN || 'http://localhost:3001';

export interface RejectInfo {
  reason?: string;
  comment?: string;
}

export interface DismissEvidenceParams {
  ideaId?: string;
  matchId?: string; // Optional: if provided, also dismiss the match.
  rejectInfo?: RejectInfo; // Optional: not required if dismissing a match.
  isGap?: boolean // Evidence is a gap or request. 
}
// List Evidences
export const listEvidences = async (
  params: { oldestFirst: boolean; filter: { ownerIds: string[] } },
  auth0: any
): Promise<any> => {
  try {
    const response = await axios.post(
      `${API_DOMAIN}/evidences/search`,
      params);
    return response.data;
  } catch (e) {
    throw e;
  }
};

// Get Idea Evidences
export const getIdeaEvidences = async (
  id: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/${id}/evidences`;
    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Get Evidence Matches
export const getEvidenceMatches = async (
  id: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/${id}/matches`;
    const response = await axios.get(url);
    return response.data.matches;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Update Evidence
export const updateEvidence = async (
  id: string,
  data: any,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/${id}`;
    const response = await axios.put(url, data); // Use PUT for full updates
    return response.data;
  } catch (e: any) {
    console.error("Error updating evidence:", e);
    throw e;
  }
};

/**
 * Update a specific field on an evidence
 *
 * @param evidenceId - The ID of the evidence to update
 * @param fieldPath - The path to the field to update (e.g., 'customField' or 'cxItem.customField')
 * @param value - The new value for the field
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the updated evidence
 */
export const updateEvidenceField = async (
  evidenceId: string,
  fieldPath: string,
  value: any,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request",
        "Missing Auth0 instance. Please check authentication.",
        ""
      );
    }

    // Build update object using the field path
    const updateData: any = {};
    const pathParts = fieldPath.split('.');
    
    // For nested paths, create nested object
    if (pathParts.length > 1) {
      let current = updateData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current[pathParts[i]] = {};
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = value;
    } else {
      updateData[fieldPath] = value;
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}`;
    const response = await axios.put(url, updateData);
    return response.data;
  } catch (e: any) {
    console.error("Error updating evidence field:", e);
    throw e;
  }
};

// Save Evidence Starred
export const saveEvidenceStarred = async (
  idea: any,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/starred`;
    const response = await axios.post(url, { idea });
    return response;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Save Evidence
export const saveEvidence = async (evidence: any, auth0: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/update`;
    const response = await axios.post(url, { evidence });
    return response;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Get Single Evidence
export const getEvidence = async (
  evidenceId: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/${evidenceId}`;
    const response = await axios.get(url);
    return response.data?.evidence;
  } catch (e) {
    throw e;
  }
};

// Get Evidence Details
export const getEvidenceDetails = async (
  evidenceId: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/evidenceDetails/${evidenceId}`;
    const response = await axios.get(url);
    // Return the evidence from response (could be response.data or response.data.evidence)
    return response.data?.evidence || response.data;
  } catch (e) {
    throw e;
  }
};

// Get Related Text Items
export const getRelatedTextItems = async (
  itemId: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/${itemId}/related-cx-items`;
    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Get Evidences Matches for an Idea
export const getEvidencesMatchesForAnIdea = async (
  id: string,
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/${id}/evidencesMatches`;
    const response = await axios.get(url);
    return response.data.matches;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

// Get Evidence URL
export const getEvidenceURL = async (id: string, auth0: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/evidences/${id}/evidence`;
    const response = await axios.get(url);
    return response.data.evidenceFromDb;
  } catch (e: any) {
    console.error(e);
    throw e;
  }
};

/**
 * Dismiss an Evidence
 *
 * @param evidenceId - The ID of the evidence to dismiss
 * @param ideaId - The ID of the idea associated with the evidence
 * @param matchId - (Optional) The ID of the match to dismiss, if applicable
 * @param rejectInfo - (Optional) Information about the rejection (may be omitted for match dismissals)
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const dismissEvidence = async (
  evidenceId: string,
  { ideaId, matchId, rejectInfo, isGap }: DismissEvidenceParams,
  auth0?: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}/dismiss`;

    // Build the payload. ideaId is required.
    // Optionally include matchId and rejectInfo if provided.
    const payload: { [key: string]: any } = { ideaId };
    if (matchId) {
      payload.matchId = matchId;
    }
    if (rejectInfo) {
      payload.rejectInfo = rejectInfo;
    }

    if (isGap) {
      payload.isGap = isGap;
    }
    
    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error dismissing evidence:", e);
    throw e;
  }
};

export interface ValidateEvidencePayload {
    ideaId: string;
    sourceOfAssignment?: "request" | "suggestion" | "bagel" | "search"
    matchId?: string;
    oldIdeaId?: string;
}

/**
 * Validate an Evidence
 *
 * @param evidenceId - The ID of the evidence to validate
 * @param payload - The payload containing ideaId and optional matchId
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const validateEvidence = async (
  evidenceId: string,
  payload: ValidateEvidencePayload,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}/validate`;

    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error validating evidence:", e);
    throw e;
  }
};

export interface EvidenceItem {
  evidenceId: any;
  matchId?: string;
  sourceOfAssignment?: "request" | "suggestion" | "bagel" | "search";
}

export interface AssignEvidencesPayload {
  ideaId: string;
  oldIdeaId?: string;
  evidences: EvidenceItem[];
}

export interface dismissEvidencesPayload {
  ideaId: string;
  evidences: EvidenceItem[];
  rejectInfo?: RejectInfo; // Optional: not required if dismissing a match.
}

/**
 * Validate multiple evidences
 *
 * @param payload - The payload containing ideaId, oldIdeaId, and an array of evidences
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const assignEvidencesToAnotherIdea = async (
  payload: AssignEvidencesPayload,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/validate`;

    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error validating multiple evidences:", e);
    throw e;
  }
};

/**
 * Dismiss multiple evidences
 *
 * @param payload - The payload containing ideaId, and an array of evidences
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const dismissEvidences = async (
  payload: dismissEvidencesPayload,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/dismiss`;

    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error dismissing multiple evidences:", e);
    throw e;
  }
};

// Evidences Page API Functions

export interface FilteredEvidencesParams {
  viewKey?: string;
  filters: {
    activeFilters: any[];
    search: string;
  };
  sort: any;
  skip: number;
  limit: number;
}

/**
 * Get filtered evidences with views support
 *
 * @param params - The parameters for filtering evidences
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the filtered evidences response
 */
export const getFilteredEvidences = async (
  params: FilteredEvidencesParams,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/filters_query`;

    console.log('🌐 API Call:', {
      url,
      params
    });

    const response = await axios.post(url, params);
    console.log('✅ API Response:', response.status, response.data);
    return response.data;
  } catch (e: any) {
    console.error("Error fetching filtered evidences:", e);
    throw e;
  }
};

/**
 * Get evidence views (user and global views)
 *
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the evidence views
 */
/**
 * Get evidence pages
 */
export const getEvidencePages = async (auth0: any): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/pages/get`;

    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error fetching evidence pages:", e);
    throw e;
  }
};

/**
 * Get evidence views, optionally filtered by page
 */
export const getEvidenceViews = async (auth0: any, pageKey?: string): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = pageKey 
      ? `${API_DOMAIN}/evidences/views/get?pageKey=${pageKey}`
      : `${API_DOMAIN}/evidences/views/get`;

    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error fetching evidence views:", e);
    throw e;
  }
};

export interface EvidenceViewPayload {
  _id?: string;
  name: string;
  filters: any[];
  stared?: boolean;
  deleted?: boolean;
  isDefault?: boolean;
  isGlobal?: boolean;
  isSubPageView?: boolean;
  pageKey?: string;
  filterObject?: string;
}

/**
 * Create or update an evidence view
 *
 * @param payload - The evidence view data
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const upsertEvidenceView = async (
  payload: EvidenceViewPayload,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    console.log('📝 [API] upsertEvidenceView - payload being sent to API:', payload);
    const url = `${API_DOMAIN}/evidences/views/add`;

    const response = await axios.post(url, payload);
    console.log('📝 [API] upsertEvidenceView - response:', response.data);
    return response.data;
  } catch (e: any) {
    console.error("Error upserting evidence view:", e);
    throw e;
  }
};

/**
 * Get evidence count for a specific view
 *
 * @param viewId - The ID of the view to get count for
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the count response { count: number }
 */
export const getViewEvidenceCount = async (
  viewId: string,
  auth0: any
): Promise<{ count: number }> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/views/${viewId}/count`;

    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error fetching view evidence count:", e);
    throw e;
  }
};

/**
 * Send evidence back to inbox - handles both unassigning from idea and undismissing
 *
 * @param evidenceId - The ID of the evidence to send back to inbox
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const sendEvidenceBackToInbox = async (
  evidenceId: string,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}/back-to-inbox`;

    const response = await axios.post(url, {});
    return response.data;
  } catch (e: any) {
    console.error("Error sending evidence back to inbox:", e);
    throw e;
  }
};

/**
 * Unassign an evidence from an idea (returns it to unassigned state)
 *
 * @param evidenceId - The ID of the evidence to unassign
 * @param ideaId - The ID of the idea to unassign from
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const unassignEvidence = async (
  evidenceId: string,
  ideaId: string,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request",
        "Missing Auth0 instance. Please check authentication.",
        ""
      );
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}/unassign`;

    const response = await axios.post(url, { ideaId });
    return response.data;
  } catch (e: any) {
    console.error("Error unassigning evidence:", e);
    throw e;
  }
};

/**
 * Reassign an evidence to another idea (uses validateEvidence endpoint with oldIdeaId)
 * Note: Shows warning modal before reassigning (handled in UI)
 *
 * @param evidenceId - The ID of the evidence to reassign
 * @param payload - The payload containing new ideaId and optional matchId/sourceOfAssignment
 * @param auth0 - Auth0 instance for authentication
 * @returns A promise resolving to the server's response
 */
export const reassignEvidence = async (
  evidenceId: string,
  payload: ValidateEvidencePayload,
  auth0: any
): Promise<any> => {
  try {
    if (!auth0) {
      throw new AuthenticationError(
        "invalid_request", 
        "Missing Auth0 instance. Please check authentication.", 
        "" 
      );
    }

    const url = `${API_DOMAIN}/evidences/${evidenceId}/validate`;

    const response = await axios.post(url, payload);
    return response.data;
  } catch (e: any) {
    console.error("Error reassigning evidence:", e);
    throw e;
  }
};
